import { Injectable } from '@angular/core'

export type FieldComparator = (current: any, initial: any) => boolean
export type FieldTransformer = (value: any) => any

export interface RelationCompareOptions {
  // Compare object by id (e.g. { id: 1, name: '...' })
  asId?: boolean
  // Compare arrays of objects by ids (e.g. [{ id: 1 }, { id: 2 }])
  asIdArray?: boolean
  // Compare arrays of primitives as sets, ignoring order (e.g. ['a','b'])
  asPrimitiveSet?: boolean
}

export interface BuildPatchOptions<T extends Record<string, any>> {
  // Include this id if present on context (e.g. from detail query or route)
  id?: unknown
  idKey?: keyof T & string // default: 'id'

  // Always-include list (required fields)
  required: Array<keyof T & string>

  // Optional fields config (included only when changed)
  optional?: Partial<Record<keyof T & string, FieldComparator | RelationCompareOptions>>

  // Per-field transforms before assigning to patch
  transform?: Partial<Record<keyof T & string, FieldTransformer>>

  // Normalize values for primitive comparison (applied to both current and initial before compare)
  normalize?: (v: any) => any // default: (v) => v ?? '' for primitives
}

@Injectable({ providedIn: 'root' })
export class PayloadBuilderService {
  /**
   * Build a minimal patch object for updates/creates.
   * - Always includes id (when provided)
   * - Always includes required fields
   * - Includes optional fields only when changed vs initial snapshot
   * - Supports relation comparison helpers (object by id, arrays by ids, arrays of primitives as sets)
   */
  buildPatch<T extends Record<string, any>>(
    current: T,
    initial: Partial<T> | null | undefined,
    options: BuildPatchOptions<T>
  ): Partial<T> {
    const {
      id,
      idKey = 'id' as keyof T & string,
      required,
      optional = {},
      transform = {},
      normalize = (v: any) => (v === undefined || v === null ? '' : v),
    } = options

    const patch: any = {}

    // id
    if (id != null) patch[idKey] = id

    // required fields
    for (const key of required) {
      const raw = current?.[key]
      patch[key] = (transform as any)?.[key] ? (transform as any)[key]!(raw) : raw
    }

    // optional fields when changed
    const hasInitial = !!initial
    for (const key of Object.keys(optional) as Array<keyof T & string>) {
      const spec = (optional as any)[key] as FieldComparator | RelationCompareOptions
      const cur = (current as any)?.[key]
      const ini = (initial as any)?.[key]

      const changed = (() => {
        if (!hasInitial) return true
        if (typeof spec === 'function') return !(spec as FieldComparator)(cur, ini)
        const c = spec as RelationCompareOptions
        if (c.asId) {
          return (cur?.id ?? null) !== (ini?.id ?? null)
        }
        if (c.asIdArray) {
          const a = Array.isArray(cur) ? cur.map((x: any) => x?.id).filter((x: any) => x != null) : []
          const b = Array.isArray(ini) ? ini.map((x: any) => x?.id).filter((x: any) => x != null) : []
          if (a.length !== b.length) return true
          const sa = [...a].sort()
          const sb = [...b].sort()
          for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return true
          return false
        }
        if (c.asPrimitiveSet) {
          const a = Array.isArray(cur) ? [...new Set(cur)] : []
          const b = Array.isArray(ini) ? [...new Set(ini)] : []
          if (a.length !== b.length) return true
          const sa = [...a].sort()
          const sb = [...b].sort()
          for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return true
          return false
        }
        // default primitive compare with normalization
        return normalize(cur) !== normalize(ini)
      })()

      if (changed) {
        const raw = cur
        patch[key] = (transform as any)?.[key] ? (transform as any)[key]!(raw) : raw
      }
    }

    return patch as Partial<T>
  }
}

// Convenience top-level function for non-injected usages (optional)
export function buildPatch<T extends Record<string, any>>(
  current: T,
  initial: Partial<T> | null | undefined,
  options: BuildPatchOptions<T>
): Partial<T> {
  const svc = new PayloadBuilderService()
  return svc.buildPatch(current, initial, options)
}
